// src/pages/AssessmentResults.jsx - PROPERLY FIXED FOR CMMC
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assessmentsApi } from '../services/api';
import PdfOptionsDialog from '../components/PdfOptionsDialog';

const AssessmentResults = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [sections, setSections] = useState([]);
  const [overallScore, setOverallScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedPrivateNotes, setExpandedPrivateNotes] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [cisoSummary, setCisoSummary] = useState('');
  const [cisoSummaryEdited, setCisoSummaryEdited] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);

  useEffect(() => {
    loadResults();
  }, [assessmentId]);

  const loadResults = async () => {
    try {
      const [assessmentResponse, responsesResponse] = await Promise.all([
        assessmentsApi.getById(assessmentId),
        assessmentsApi.getResponses(assessmentId)
      ]);

      const assessmentData = assessmentResponse.data;
      
      // Convert score strings to numbers
      if (assessmentData.scores) {
        assessmentData.scores = assessmentData.scores.map(score => ({
          ...score,
          actual_score: score.actual_score ? parseFloat(score.actual_score) : 0,
          max_possible_score: score.max_possible_score ? parseFloat(score.max_possible_score) : 0,
          weighted_score: score.weighted_score ? parseFloat(score.weighted_score) : 0,
          total_questions: score.total_questions || 0,
          answered_questions: score.answered_questions || 0,
          met_count: score.met_count || 0,
          partially_met_count: score.partially_met_count || 0,
          not_met_count: score.not_met_count || 0,
          na_count: score.na_count || 0
        }));
      }
      
      setAssessment(assessmentData);
      setSections(responsesResponse.data.sections || []);

      const overall = assessmentData.scores?.find(s => s.section_id === null);
      setOverallScore(overall);

      if (assessmentData.ciso_summary) {
        setCisoSummary(assessmentData.ciso_summary);
        setCisoSummaryEdited(!!assessmentData.ciso_summary_edited);
      }
    } catch (error) {
      setError('Failed to load results');
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const togglePrivateNotes = (questionId) => {
    setExpandedPrivateNotes(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#27ae60';
    if (percentage >= 60) return '#f39c12';
    return '#e74c3c';
  };

  const getAnswerBadge = (answer) => {
    const styles = {
      met: { bg: '#27ae60', label: 'Met' },
      partially_met: { bg: '#f39c12', label: 'Partially Met' },
      not_met: { bg: '#e74c3c', label: 'Not Met' },
      na: { bg: '#95a5a6', label: 'N/A' }
    };
    const style = styles[answer] || { bg: '#ccc', label: 'Unanswered' };
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: style.bg,
        color: 'white'
      }}>
        {style.label}
      </span>
    );
  };

  const calculateSectionStats = (section) => {
    let totalWeight = 0;
    let earnedScore = 0;
    let maxPossible = 0;
    const answerCounts = { met: 0, partially_met: 0, not_met: 0, na: 0 };

    section.questions?.forEach(question => {
      const weight = parseFloat(question.weight) || 1.0;
      
      if (question.answer) {
        answerCounts[question.answer]++;
        
        if (question.answer !== 'na') {
          maxPossible += weight;
          if (question.answer === 'met') {
            earnedScore += weight;
          } else if (question.answer === 'partially_met') {
            earnedScore += weight * 0.5;
          }
        }
      } else {
        maxPossible += weight;
      }
      
      totalWeight += weight;
    });

    const percentage = maxPossible > 0 ? Math.round((earnedScore / maxPossible) * 100) : 0;

    return { 
      earnedScore: earnedScore || 0, 
      maxPossible: maxPossible || 0, 
      percentage, 
      answerCounts, 
      totalWeight: totalWeight || 0 
    };
  };

  // Check if this is a CMMC assessment
  const isCMMC = assessment?.assessment_type_name?.includes('CMMC');
  
  // Check if this is NIST CSF 2.0
  const isCSF20 = assessment?.assessment_type_name?.includes('2.0');

  // Extract domain code from section name like "Access Control (AC.L1-3.1.1[a])" -> "AC"
  const extractDomainCode = (sectionName) => {
    const match = sectionName.match(/\(([A-Z]{2})\./);
    return match ? match[1] : null;
  };

  // Extract subcategory from section name like "Access Control (AC.L1-3.1.1[a])" -> "AC.L1-3.1.1"
  const extractSubcategory = (sectionName) => {
    const match = sectionName.match(/\(([A-Z]{2}\.L\d+-\d+\.\d+\.\d+)/);
    return match ? match[1] : null;
  };

  // Get domain name from section name like "Access Control (AC.L1-3.1.1[a])" -> "Access Control"
  const extractDomainName = (sectionName) => {
    const match = sectionName.match(/^(.+?)\s*\(/);
    return match ? match[1].trim() : sectionName;
  };

  // Group sections by subcategory (Level 2) for detailed results
  const groupBySubcategory = () => {
    const grouped = {};
    
    sections.forEach(section => {
      const subcategory = extractSubcategory(section.name);
      if (subcategory) {
        if (!grouped[subcategory]) {
          grouped[subcategory] = {
            name: section.name.replace(/\[[a-z]\]/, '').trim(), // Remove [a], [b], etc
            subcategory,
            subsections: [],
            allQuestions: []
          };
        }
        grouped[subcategory].subsections.push(section);
        if (section.questions) {
          grouped[subcategory].allQuestions.push(...section.questions);
        }
      }
    });

    return Object.values(grouped);
  };

  // Calculate CMMC domain scores (Level 1)
  const calculateCMMCDomains = () => {
    const domainColors = {
      'AC': '#3498DB', 'AT': '#9B59B6', 'AU': '#E67E22', 'CA': '#1ABC9C',
      'CM': '#34495E', 'IA': '#E74C3C', 'IR': '#F39C12', 'MA': '#16A085',
      'MP': '#8E44AD', 'PE': '#2980B9', 'PS': '#27AE60', 'RA': '#C0392B',
      'SC': '#D35400', 'SI': '#2C3E50'
    };

    const domainNames = {
      'AC': 'Access Control',
      'AT': 'Awareness and Training', 
      'AU': 'Audit and Accountability',
      'CA': 'Assessment, Authorization, and Monitoring',
      'CM': 'Configuration Management',
      'IA': 'Identification and Authentication',
      'IR': 'Incident Response',
      'MA': 'Maintenance',
      'MP': 'Media Protection',
      'PE': 'Physical Protection',
      'PS': 'Personnel Security',
      'RA': 'Risk Assessment',
      'SC': 'System and Communications Protection',
      'SI': 'System and Information Integrity'
    };

    const domains = {};

    sections.forEach(section => {
      const code = extractDomainCode(section.name);
      if (code) {
        if (!domains[code]) {
          domains[code] = {
            code,
            name: domainNames[code] || code,
            color: domainColors[code] || '#666',
            sections: [],
            totalEarned: 0,
            totalPossible: 0,
            met: 0,
            partial: 0,
            notMet: 0,
            na: 0
          };
        }
        
        domains[code].sections.push(section);
        const stats = calculateSectionStats(section);
        domains[code].totalEarned += stats.earnedScore;
        domains[code].totalPossible += stats.maxPossible;
        domains[code].met += stats.answerCounts.met;
        domains[code].partial += stats.answerCounts.partially_met;
        domains[code].notMet += stats.answerCounts.not_met;
        domains[code].na += stats.answerCounts.na;
      }
    });

    return Object.values(domains).map(domain => ({
      ...domain,
      percentage: domain.totalPossible > 0 ? Math.round((domain.totalEarned / domain.totalPossible) * 100) : 0
    })).sort((a, b) => a.code.localeCompare(b.code));
  };

  // Calculate stats for a grouped subcategory
  const calculateGroupedStats = (group) => {
    let totalEarned = 0;
    let totalPossible = 0;
    const answerCounts = { met: 0, partially_met: 0, not_met: 0, na: 0 };

    group.allQuestions.forEach(question => {
      const weight = parseFloat(question.weight) || 1.0;
      
      if (question.answer) {
        answerCounts[question.answer]++;
        
        if (question.answer !== 'na') {
          totalPossible += weight;
          if (question.answer === 'met') {
            totalEarned += weight;
          } else if (question.answer === 'partially_met') {
            totalEarned += weight * 0.5;
          }
        }
      } else {
        totalPossible += weight;
      }
    });

    const percentage = totalPossible > 0 ? Math.round((totalEarned / totalPossible) * 100) : 0;

    return { 
      earnedScore: totalEarned, 
      maxPossible: totalPossible, 
      percentage, 
      answerCounts 
    };
  };

  // Calculate NIST domains (handles both CSF 1.1 and 2.0)
  const calculateNISTDomains = () => {
    // CSF 2.0 has 6 functions including GOVERN
    const domainMapCSF20 = {
      'Govern': { name: 'Govern', color: '#F1C40F', codePrefix: 'GV.', image: '/images/NIST_20_Govern.png', sections: [] },
      'Identify': { name: 'Identify', color: '#5DADE2', codePrefix: 'ID.', image: '/images/NIST_20_Identify.png', sections: [] },
      'Protect': { name: 'Protect', color: '#9B59B6', codePrefix: 'PR.', image: '/images/NIST_20_Protect.png', sections: [] },
      'Detect': { name: 'Detect', color: '#F39C12', codePrefix: 'DE.', image: '/images/NIST_20_Detect.png', sections: [] },
      'Respond': { name: 'Respond', color: '#E74C3C', codePrefix: 'RS.', image: '/images/NIST_20_Respond.png', sections: [] },
      'Recover': { name: 'Recover', color: '#27AE60', codePrefix: 'RC.', image: '/images/NIST_20_Recover.png', sections: [] }
    };
    
    // CSF 1.1 has 5 functions (no GOVERN)
    const domainMapCSF11 = {
      'Identify': { name: 'Identify', color: '#5DADE2', codePrefix: 'ID.', image: '/images/identify.png', sections: [] },
      'Protect': { name: 'Protect', color: '#9B59B6', codePrefix: 'PR.', image: '/images/protect.png', sections: [] },
      'Detect': { name: 'Detect', color: '#F39C12', codePrefix: 'DE.', image: '/images/detect.png', sections: [] },
      'Respond': { name: 'Respond', color: '#E74C3C', codePrefix: 'RS.', image: '/images/respond.png', sections: [] },
      'Recover': { name: 'Recover', color: '#27AE60', codePrefix: 'RC.', image: '/images/recover.png', sections: [] }
    };
    
    // Use appropriate domain map based on CSF version
    const domainMap = isCSF20 ? domainMapCSF20 : domainMapCSF11;

    sections.forEach(section => {
      const sectionName = section.name || '';
      Object.values(domainMap).forEach(domain => {
        if (sectionName.includes(`(${domain.codePrefix}`)) {
          domain.sections.push(section);
        }
      });
    });

    const domainScores = [];
    Object.values(domainMap).forEach(domain => {
      if (domain.sections.length > 0) {
        let totalEarned = 0;
        let totalPossible = 0;
        
        domain.sections.forEach(section => {
          const stats = calculateSectionStats(section);
          totalEarned += stats.earnedScore;
          totalPossible += stats.maxPossible;
        });

        const percentage = totalPossible > 0 ? Math.round((totalEarned / totalPossible) * 100) : 0;
        
        domainScores.push({
          name: domain.name,
          color: domain.color,
          image: domain.image,
          percentage,
          earnedScore: totalEarned,
          maxPossible: totalPossible
        });
      }
    });

    return domainScores;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !assessment) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error || 'Assessment not found'}</p>
        <button onClick={() => navigate('/assessments')}>Back to Assessments</button>
      </div>
    );
  }

  const handleGeneratePdf = async (options) => {
    try {
      setActionLoading(true);
      
      // Build query parameters from options
      const params = new URLSearchParams({
        include_met: options.include_met.toString(),
        include_not_met: options.include_not_met.toString(),
        include_partially_met: options.include_partially_met.toString(),
        include_na: options.include_na.toString(),
        include_private_notes: options.include_private_notes.toString(),
        sort_by: options.sort_by
      });

      // Call API to generate PDF
      const response = await assessmentsApi.generatePdf(assessmentId, params.toString());
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment_report_${assessment.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('PDF report generated successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateSSP = async () => {
    try {
      setActionLoading(true);
      const response = await assessmentsApi.generateSSP(assessmentId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `SSP_${assessment.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('✅ System Security Plan generated successfully!');
    } catch (error) {
      console.error('Error generating SSP:', error);
      alert('❌ Error generating SSP: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleGeneratePOAM = async () => {
    try {
      setActionLoading(true);
      const response = await assessmentsApi.generatePOAM(assessmentId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `POAM_${assessment.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('✅ Plan of Action & Milestones generated successfully!');
    } catch (error) {
      console.error('Error generating PoAM:', error);
      alert('❌ Error generating PoAM: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (cisoSummary && cisoSummaryEdited) {
      if (!window.confirm('You have edited this CISO summary manually. Analyzing again will overwrite your changes. Continue?')) return;
    } else if (cisoSummary) {
      if (!window.confirm('This will replace the existing AI-generated summary with a new one. Continue?')) return;
    }
    try {
      setAnalyzing(true);
      const response = await assessmentsApi.analyzeCisoSummary(assessmentId);
      setCisoSummary(response.data.ciso_summary);
      setCisoSummaryEdited(false);
      setEditingSummary(false);
      alert('CISO summary generated successfully!');
    } catch (err) {
      alert('Analysis failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveSummary = async () => {
    try {
      setSavingSummary(true);
      await assessmentsApi.updateCisoSummary(assessmentId, cisoSummary);
      setCisoSummaryEdited(true);
      setEditingSummary(false);
    } catch (err) {
      alert('Failed to save summary: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingSummary(false);
    }
  };

  const groupedSections = isCMMC ? groupBySubcategory() : sections;

  return (
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h1 style={{ margin: 0, marginBottom: '10px' }}>{assessment.name}</h1>
          <div style={{ color: '#666', fontSize: '16px' }}>
            Assessment Results - {assessment.assessment_type_name}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link
            to={`/assessments/${assessmentId}`}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            ← Back to Assessment
          </Link>
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            style={{
              padding: '10px 20px',
              backgroundColor: analyzing ? '#999' : '#6B3FA0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: analyzing ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {analyzing ? '⏳ Analyzing...' : '🤖 Analyze with AI'}
          </button>
          <button
            onClick={() => setPdfDialogOpen(true)}
            disabled={actionLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: actionLoading ? '#999' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {actionLoading ? 'Generating...' : '📄 Generate PDF Report'}
          </button>
        </div>
      </div>

      {/* Overall Score Card */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        marginBottom: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Overall Assessment Score</h2>

        {overallScore && (
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              fontSize: '72px',
              fontWeight: 'bold',
              color: getScoreColor(overallScore.weighted_score || 0)
            }}>
              {Math.round(overallScore.weighted_score || 0)}%
            </div>
            <div style={{ fontSize: '18px', color: '#666', marginTop: '10px' }}>
              {(overallScore.actual_score || 0).toFixed(2)} / {(overallScore.max_possible_score || 0).toFixed(2)} points
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        {overallScore && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginTop: '30px'
          }}>
            <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#d4edda', borderRadius: '8px' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>
                {overallScore.met_count || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Met</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>
                {overallScore.partially_met_count || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Partially Met</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8d7da', borderRadius: '8px' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e74c3c' }}>
                {overallScore.not_met_count || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Not Met</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#e9ecef', borderRadius: '8px' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#95a5a6' }}>
                {overallScore.na_count || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>N/A</div>
            </div>
          </div>
        )}
      </div>

      {/* CISO Executive Summary Card */}
      {cisoSummary && (
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #6B3FA0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>🤖 CISO Executive Summary</h2>
              {cisoSummaryEdited && (
                <span style={{
                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                  fontWeight: 'bold', backgroundColor: '#e8f4fd', color: '#1976d2'
                }}>Edited</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {editingSummary ? (
                <>
                  <button
                    onClick={() => setEditingSummary(false)}
                    style={{ padding: '6px 14px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: 'white' }}
                  >Cancel</button>
                  <button
                    onClick={handleSaveSummary}
                    disabled={savingSummary}
                    style={{ padding: '6px 14px', backgroundColor: '#6B3FA0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >{savingSummary ? 'Saving...' : '💾 Save'}</button>
                </>
              ) : (
                <button
                  onClick={() => setEditingSummary(true)}
                  style={{ padding: '6px 14px', border: '1px solid #6B3FA0', color: '#6B3FA0', borderRadius: '4px', cursor: 'pointer', background: 'white' }}
                >✏️ Edit</button>
              )}
            </div>
          </div>
          {editingSummary ? (
            <textarea
              value={cisoSummary}
              onChange={(e) => setCisoSummary(e.target.value)}
              rows={16}
              style={{
                width: '100%', padding: '12px', fontSize: '14px', lineHeight: '1.6',
                border: '1px solid #6B3FA0', borderRadius: '4px', fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box'
              }}
            />
          ) : (
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.7', color: '#333' }}>
              {cisoSummary}
            </div>
          )}
        </div>
      )}

      {/* CMMC Compliance Reports - INLINE (No separate component with 404 issues) */}
      {isCMMC && assessment.status === 'completed' && (
        <div style={{
          marginBottom: '20px',
          padding: '25px',
          backgroundColor: '#F3EEFA',
          borderRadius: '8px',
          border: '2px solid #6B3FA0',
          boxShadow: '0 2px 8px rgba(107, 63, 160, 0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '2px solid #6B3FA0'
          }}>
            <span style={{ fontSize: '24px', marginRight: '10px' }}>📋</span>
            <h3 style={{ 
              margin: 0, 
              color: '#4A2B70',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              CMMC Level 2 Compliance Reports
            </h3>
          </div>

          <p style={{ 
            margin: '0 0 20px 0', 
            color: '#4A2B70',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Generate official compliance documentation required for CMMC Level 2 certification and C3PAO assessment.
          </p>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px'
          }}>
            {/* SSP Button */}
            <button
              onClick={handleGenerateSSP}
              disabled={actionLoading}
              style={{
                padding: '20px',
                backgroundColor: actionLoading ? '#9B7BB8' : '#6B3FA0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(107, 63, 160, 0.3)',
                textAlign: 'left'
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>📄</div>
              <div style={{ fontSize: '16px', marginBottom: '5px' }}>System Security Plan (SSP)</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Comprehensive Word document with all 110 controls</div>
            </button>

            {/* PoAM Button */}
            <button
              onClick={handleGeneratePOAM}
              disabled={actionLoading}
              style={{
                padding: '20px',
                backgroundColor: actionLoading ? '#52BE80' : '#27AE60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(39, 174, 96, 0.3)',
                textAlign: 'left'
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>📊</div>
              <div style={{ fontSize: '16px', marginBottom: '5px' }}>Plan of Action & Milestones</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                Excel workbook tracking {overallScore.not_met_count + overallScore.partially_met_count} gap{(overallScore.not_met_count + overallScore.partially_met_count) !== 1 ? 's' : ''}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Domain Summary */}
      {sections.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
            {isCMMC ? 'CMMC Level 2 Domains' : 'NIST Core Functions'}
          </h2>
          
          {isCMMC ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              {calculateCMMCDomains().map(domain => (
                <div
                  key={domain.code}
                  style={{
                    padding: '20px',
                    border: `2px solid ${domain.color}`,
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h3 style={{ 
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: domain.color
                    }}>
                      {domain.code}
                    </h3>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: getScoreColor(domain.percentage)
                    }}>
                      {domain.percentage}%
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '10px', color: '#333' }}>
                    {domain.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666' }}>
                    {domain.totalEarned.toFixed(1)} / {domain.totalPossible.toFixed(1)} pts
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#999',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #ddd'
                  }}>
                    ✓ {domain.met} | ◐ {domain.partial} | ✗ {domain.notMet} | N/A {domain.na}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              {calculateNISTDomains().map(domain => (
                <div
                  key={domain.name}
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <img 
                    src={domain.image} 
                    alt={`${domain.name} NIST Wheel`}
                    style={{ 
                      width: '150px', 
                      height: '150px',
                      marginBottom: '10px'
                    }}
                  />
                  <h3 style={{ 
                    margin: '15px 0 10px 0', 
                    color: domain.color,
                    fontSize: '18px'
                  }}>
                    {domain.name}
                  </h3>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: getScoreColor(domain.percentage),
                    marginBottom: '5px'
                  }}>
                    {domain.percentage}%
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {domain.earnedScore.toFixed(1)} / {domain.maxPossible.toFixed(1)} points
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section Breakdown */}
      <div>
        <h2 style={{ marginBottom: '20px' }}>Detailed Results by {isCMMC ? 'Subcategory' : 'Category'}</h2>
        {groupedSections.map((section, idx) => {
          const stats = isCMMC ? calculateGroupedStats(section) : calculateSectionStats(section);
          const sectionId = isCMMC ? section.subcategory : section.id;
          const isExpanded = expandedSections[sectionId];

          return (
            <div
              key={isCMMC ? section.subcategory : section.id}
              style={{
                backgroundColor: 'white',
                marginBottom: '10px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              {/* Section Header */}
              <div
                onClick={() => toggleSection(sectionId)}
                style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: isExpanded ? '1px solid #dee2e6' : 'none'
                }}
              >
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, marginBottom: '5px' }}>{section.name}</h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Met: {stats.answerCounts.met} | Partially: {stats.answerCounts.partially_met} | 
                    Not Met: {stats.answerCounts.not_met} | N/A: {stats.answerCounts.na}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: getScoreColor(stats.percentage)
                    }}>
                      {stats.percentage}%
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {(stats.earnedScore || 0).toFixed(1)} / {(stats.maxPossible || 0).toFixed(1)} points
                    </div>
                  </div>
                  <div style={{
                    fontSize: '24px',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Questions Detail */}
              {isExpanded && (
                <div style={{ padding: '20px' }}>
                  {(isCMMC ? section.allQuestions : section.questions || []).map((question, index) => (
                    <div
                      key={question.id}
                      style={{
                        padding: '15px',
                        marginBottom: '10px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            {index + 1}. {question.question_text}
                          </div>
                          {question.subcategory_code && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                              {question.subcategory_code}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Weight: {question.weight}
                          </div>
                        </div>
                        <div style={{ marginLeft: '20px' }}>
                          {getAnswerBadge(question.answer)}
                        </div>
                      </div>
                      
                      {question.notes && (
                        <div style={{
                          marginTop: '10px',
                          padding: '10px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontStyle: 'italic',
                          color: '#666'
                        }}>
                          <strong>Notes:</strong> {question.notes}
                        </div>
                      )}
                      
                      {question.private_notes && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => togglePrivateNotes(question.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#FFF9E6',
                              border: '1px solid #FFE082',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#F57C00',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <span style={{ 
                              transform: expandedPrivateNotes[question.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              display: 'inline-block'
                            }}>
                              ▼
                            </span>
                            🔒 Private Notes (Internal)
                            {!expandedPrivateNotes[question.id] && (
                              <span style={{ 
                                marginLeft: '5px',
                                fontSize: '10px',
                                backgroundColor: '#F57C00',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '10px'
                              }}>
                                Click to view
                              </span>
                            )}
                          </button>
                          {expandedPrivateNotes[question.id] && (
                            <div style={{
                              marginTop: '5px',
                              padding: '10px',
                              backgroundColor: '#FFF9E6',
                              border: '1px solid #FFE082',
                              borderRadius: '4px',
                              fontSize: '14px',
                              color: '#666'
                            }}>
                              {question.private_notes}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* PDF Options Dialog */}
      <PdfOptionsDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        assessmentId={assessmentId}
        onGenerate={handleGeneratePdf}
      />
    </div>
  );
};

export default AssessmentResults;
